import { NgIf } from "@angular/common";
import { Component, OnInit } from "@angular/core";

import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { ActivatedRoute, Router } from "@angular/router";
import { IApiResponse } from "@models/api-response.model";
import { ISessionData } from "@models/user.model";
import { AuthFirebaseService } from "@services/auth-firebase/auth-firebase.service";
import { AppStateService, RequestService } from "@services/index";
import { NotificationService } from "@services/notification/notification.service";

const RESEND_COOLDOWN_SECONDS = 30;

@Component({
  selector: "fh-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.css"],
  standalone: true,
  imports: [MatButtonModule, MatIconModule, FormsModule, MatInputModule, NgIf],
})
export class LoginComponent implements OnInit {
  email = "";
  emailErr = "";
  code = "";
  codeErr = "";
  authEror = "";
  infoMessage = "";
  showOTP = false;
  isSubmitting = false;
  isGoogleSigningIn = false;
  resendAvailableInSeconds = 0;
  private resendTimer: ReturnType<typeof setInterval> | undefined;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private auth: RequestService,
    private appStateService: AppStateService,
    private authFirebaseService: AuthFirebaseService,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    // A session cookie from a prior visit may still be valid — check live rather than
    // trusting a stale localStorage flag (there's no more token to read from storage).
    this.appStateService.checkSession().subscribe((isAuth) => {
      if (isAuth) {
        this.router.navigate(["/"]);
      }
    });

    // Magic-link support: a click from the verification email lands back
    // here with ?code=&email= already filled in — verify immediately.
    const params = this.route.snapshot.queryParamMap;
    const linkEmail = params.get("email");
    const linkCode = params.get("code");
    if (linkEmail && linkCode) {
      this.email = linkEmail;
      this.code = linkCode;
      this.showOTP = true;
      this.verifyCode();
    }
  }

  keyDownFunction(event: { keyCode: number }) {
    if (event.keyCode !== 13) return;
    if (this.showOTP) {
      this.verifyCode();
    } else {
      this.sendCode();
    }
  }

  sendCode() {
    this.emailErr = "";
    this.authEror = "";
    this.infoMessage = "";
    if (!this.email || !this.email.trim()) {
      this.emailErr = "Email is required.";
      return;
    }

    this.isSubmitting = true;
    this.auth
      .post<IApiResponse<unknown>>("/api/v1/auth/send-verification-code", { email: this.email })
      .subscribe({
        next: (res) => {
          this.isSubmitting = false;
          if (res.status === true) {
            this.showOTP = true;
            this.startResendCooldown();
          } else {
            const retryAfter = (res.data as { retryAfterSeconds?: number } | null)
              ?.retryAfterSeconds;
            if (retryAfter) {
              this.showOTP = true;
              this.startResendCooldown(retryAfter);
            }
            this.authEror = res.message || "Failed to send verification code.";
          }
        },
        error: () => {
          this.isSubmitting = false;
          this.authEror = "Something went wrong. Please try again.";
        },
      });
  }

  resendCode() {
    if (this.resendAvailableInSeconds > 0) return;
    this.sendCode();
  }

  changeEmail() {
    this.showOTP = false;
    this.code = "";
    this.codeErr = "";
    this.authEror = "";
    this.infoMessage = "";
  }

  verifyCode() {
    this.codeErr = "";
    this.authEror = "";
    this.infoMessage = "";
    if (!this.code || !this.code.trim()) {
      this.codeErr = "Verification code is required.";
      return;
    }

    this.isSubmitting = true;
    this.auth
      .post<IApiResponse<ISessionData>>("/api/v1/auth/verify-code", {
        email: this.email,
        code: this.code,
      })
      .subscribe({
        next: (res: any) => {
          this.isSubmitting = false;
          if (res.status === true) {
            this.appStateService.setUserSession(res.data);
            this.router.navigate(["/dashboard"]);
          } else if (res.data?.pendingActivation) {
            this.infoMessage =
              res.message ||
              "Account created — pending activation. An existing admin needs to activate it before you can sign in.";
          } else {
            this.authEror = res.message || "Invalid verification code.";
          }
        },
        error: () => {
          this.isSubmitting = false;
          this.authEror = "Something went wrong. Please try again.";
        },
      });
  }

  async signInWithGoogle() {
    this.authEror = "";
    this.infoMessage = "";
    this.isGoogleSigningIn = true;
    try {
      const idToken = await this.authFirebaseService.signInWithGoogle();
      this.auth
        .post<IApiResponse<ISessionData>>("/api/v1/auth/google-login", { idToken })
        .subscribe({
          next: (res: any) => {
            this.isGoogleSigningIn = false;
            if (res.status === true) {
              this.appStateService.setUserSession(res.data);
              this.router.navigate(["/dashboard"]);
            } else if (res.data?.pendingActivation) {
              // A long duration -- this is telling them their account is pending
              // activation, not a routine confirmation toast they can glance past.
              this.notificationService.show({
                message:
                  res.message || res.data.upgradeMessage || "Account created — pending activation.",
                type: "info",
                duration: 10000,
              });
              this.infoMessage =
                res.message || res.data.upgradeMessage || "Account created — pending activation.";
            } else {
              this.authEror = res.message;
            }
          },
          error: () => {
            this.isGoogleSigningIn = false;
            this.authEror = "Google sign-in failed. Please try again.";
          },
        });
    } catch (error) {
      console.error("Google sign-in error:", error);
      this.isGoogleSigningIn = false;
      this.authEror = "Google sign-in was cancelled or failed.";
    }
  }

  private startResendCooldown(seconds: number = RESEND_COOLDOWN_SECONDS) {
    this.resendAvailableInSeconds = Math.max(1, Math.ceil(seconds));
    if (this.resendTimer) clearInterval(this.resendTimer);
    this.resendTimer = setInterval(() => {
      this.resendAvailableInSeconds -= 1;
      if (this.resendAvailableInSeconds <= 0 && this.resendTimer) {
        clearInterval(this.resendTimer);
      }
    }, 1000);
  }
}
